import React, { useMemo, useState } from "react";
import { Siren, CaretDown, ArrowSquareOut, Plus, Barricade, Fire, ClockCounterClockwise } from "@phosphor-icons/react";
import { EVAC_LEVEL_META, evacStatusMeta } from "./map/SheriffEvacLayer";
import { updateEvacAlert } from "../lib/api";
import { relTime } from "../lib/incidents";
import { distMi } from "../lib/followedFires";
import { zoneClusterPrefix, fireNameInTitle } from "../lib/shareCaption";
import { useAuth } from "../lib/AuthContext";
import { useAutoExpandNew } from "../lib/useAutoExpandNew";
import { closuresNearEvac } from "../lib/evacRoads";
import PerimeterMapModal from "./PerimeterMapModal";
import EvacZoneTimeline from "./EvacZoneTimeline";

const fmtDT = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function SheriffEvacCard({ alerts = [], closures = [], wsdotAlerts = [], fires = [], onChanged, onPost, onFlyTo, onClosureSelect, scrollList = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [lifting, setLifting] = useState(null);
  const [perimUrl, setPerimUrl] = useState(null);
  const [openRoads, setOpenRoads] = useState(() => new Set());
  const [openTimelines, setOpenTimelines] = useState(() => new Set());
  const [pastOpen, setPastOpen] = useState(false);
  const canPost = user && (user.role === "admin" || user.verified);

  const roadsFor = (a) => closuresNearEvac(a, closures, wsdotAlerts);

  const liveAlerts = useMemo(() => alerts.filter((a) => a.active !== false), [alerts]);
  const pastAlerts = useMemo(
    () =>
      alerts
        .filter((a) => a.active === false)
        .sort((x, y) => new Date(y.lifted_at || y.updated_at || 0) - new Date(x.lifted_at || x.updated_at || 0)),
    [alerts],
  );
  useAutoExpandNew(liveAlerts.map((a) => a.id), "okwatch:evac:seen", setOpen);

  // Group alerts by the nearest matching fire (zone radius + 10 mi), newest
  // first within each group. Named/acreage fires win over smoke-check dups.
  const groups = useMemo(() => {
    const newest = (list) =>
      [...list].sort((x, y) => new Date(y.created_at || 0) - new Date(x.created_at || 0));
    if (!fires.length) return [{ key: "flat", title: null, alerts: newest(liveAlerts) }];
    const byKey = new Map();
    const put = (best, a) => {
      const key = best ? (best.name || "").toUpperCase() : "__other__";
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          title: best ? (best.name || "").toUpperCase() : "OTHER / AREA-WIDE",
          alerts: [],
        });
      }
      byKey.get(key).alerts.push(a);
      return key;
    };
    const prefixToKey = new Map();
    const undecided = [];
    // Pass 1: zone title references the fire (name or initials) — wins outright.
    for (const a of liveAlerts) {
      const best =
        fires.find(
          (f) =>
            f.name &&
            ((a.fire_names || []).includes(f.name.trim().toLowerCase()) ||
              fireNameInTitle(f.name, a.title)) &&
            (a.latitude == null || f.latitude == null ||
              distMi(a.latitude, a.longitude, f.latitude, f.longitude) <= 60)
        ) || null;
      if (best) {
        const key = put(best, a);
        const p = zoneClusterPrefix(a.title);
        if (p && !prefixToKey.has(p)) prefixToKey.set(p, key);
      } else {
        undecided.push(a);
      }
    }
    // Pass 2: nearest fire within zone radius + 10 mi (sized fires preferred).
    const leftovers = [];
    for (const a of undecided) {
      let best = null;
      if (a.latitude != null) {
        let cands = [];
        for (const f of fires) {
          if (f.latitude == null || !f.name) continue;
          const d = distMi(a.latitude, a.longitude, f.latitude, f.longitude);
          if (d <= (Number(a.radius_mi) || 1) + 10) cands.push({ f, d });
        }
        const sized = cands.filter((c) => parseFloat(c.f.acres ?? c.f.incident_size) >= 10);
        best = (sized.length ? sized : cands).sort((x, y) => x.d - y.d)[0]?.f || null;
      }
      if (best) {
        const key = put(best, a);
        const p = zoneClusterPrefix(a.title);
        if (p && !prefixToKey.has(p)) prefixToKey.set(p, key);
      } else {
        leftovers.push(a);
      }
    }
    // Pass 3: unmatched zones sharing a title prefix with a matched zone join its fire.
    for (const a of leftovers) {
      const p = zoneClusterPrefix(a.title);
      const key = p && prefixToKey.get(p);
      if (key) byKey.get(key).alerts.push(a);
      else put(null, a);
    }
    const gs = [...byKey.values()].map((g) => ({ ...g, alerts: newest(g.alerts) }));
    gs.sort((x, y) => {
      if (x.key === "__other__") return 1;
      if (y.key === "__other__") return -1;
      return new Date(y.alerts[0].created_at || 0) - new Date(x.alerts[0].created_at || 0);
    });
    return gs;
  }, [liveAlerts, fires]);

  const toggleRoads = (id) =>
    setOpenRoads((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleTimeline = (id) =>
    setOpenTimelines((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (alerts.length === 0 && !canPost) return null;

  const lift = async (id) => {
    setLifting(id);
    try {
      await updateEvacAlert(id, { active: false });
      onChanged?.();
    } finally {
      setLifting(null);
    }
  };

  const worst = liveAlerts.reduce(
    (w, a) =>
      a.active !== false && (!w || (a.level || 0) > (w.level || 0)) ? a : w,
    null,
  );
  const liveCount = liveAlerts.length;
  const headerColor = worst ? EVAC_LEVEL_META[worst.level].color : "#979F91";

  return (
    <section
      data-testid="sheriff-evac-card"
      className="border-b bg-tac-surface/40"
      style={alerts.length > 0 ? { borderColor: `${headerColor}66` } : { borderColor: "#28322D" }}
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2.5">
        <button
          type="button"
          data-testid="sheriff-evac-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          <Siren
            size={14}
            weight="fill"
            className={liveCount > 0 ? "animate-pulse" : ""}
            style={{ color: liveCount > 0 ? headerColor : "#979F91" }}
          />
          <span className="text-[12px] uppercase tracking-[0.15em] font-bold text-tac-text">
            Sheriff / EM Evacuations
          </span>
          {liveCount > 0 && (
            <span className="text-[15px] font-mono-tac tabular" style={{ color: headerColor }}>
              · {liveCount}
            </span>
          )}
          <CaretDown
            size={12}
            weight="bold"
            className={`text-tac-dim transition-transform ml-auto ${open ? "rotate-180" : ""}`}
          />
        </button>
        {canPost && onPost && (
          <button
            type="button"
            data-testid="sheriff-evac-post-btn"
            onClick={onPost}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold uppercase tracking-wider border border-tac-danger/60 bg-tac-danger/15 text-tac-danger hover:bg-tac-danger hover:text-white rounded-sm transition-colors shrink-0"
          >
            <Plus size={10} weight="bold" /> Post
          </button>
        )}
      </div>

      {open && liveAlerts.length === 0 && (
        <div className="px-4 pb-3 text-[14px] text-tac-faint" data-testid="sheriff-evac-empty">
          No active sheriff/EM evacuation alerts.
        </div>
      )}

      {open && liveAlerts.length > 0 && (
        <ul
          className={`divide-y divide-tac-border/50 ${scrollList ? "max-h-[340px] overflow-y-auto tac-scroll" : ""}`}
          data-testid="sheriff-evac-list"
        >
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              {g.title && (
                <li
                  data-testid={`evac-group-${g.key.replace(/[^A-Z0-9]+/g, "-")}`}
                  className="px-4 py-1.5 bg-tac-surface/90 sticky top-0 z-10 flex items-center gap-1.5 border-y border-tac-border/60"
                >
                  <Fire size={12} weight="fill" className="text-tac-primary shrink-0" />
                  <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-tac-primary truncate">
                    {g.title}
                  </span>
                  <span className="text-[12px] font-mono-tac text-tac-faint">· {g.alerts.length}</span>
                </li>
              )}
              {g.alerts.map((a) => {
            const m = EVAC_LEVEL_META[a.level] || EVAC_LEVEL_META[1];
            const sMeta = evacStatusMeta(a);
            const inactive = a.active === false || a.status === "rescinded" || a.status === "historical";
            const roads = roadsFor(a);
            const roadsOpen = openRoads.has(a.id);
            return (
              <li
                key={a.id}
                data-testid={`sheriff-evac-${a.id}`}
                className={`px-4 py-2.5 ${inactive ? "opacity-60" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => onFlyTo?.(a.latitude, a.longitude, a.id)}
                  className="w-full text-left group"
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                      className="px-1.5 py-0.5 text-[12px] font-bold uppercase tracking-wider rounded-sm border"
                      style={{ color: m.color, borderColor: `${m.color}66`, background: `${m.color}18` }}
                    >
                      {m.label}
                    </span>
                    <span
                      data-testid={`evac-status-chip-${a.id}`}
                      className="text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: sMeta.color }}
                    >
                      {sMeta.label}
                    </span>
                    <span className="text-[12px] font-mono-tac text-tac-faint ml-auto" data-testid={`evac-time-${a.id}`}>
                      {relTime(a.created_at)} · {fmtDT(a.created_at)}
                    </span>
                  </div>
                  <div className="text-[15px] font-semibold text-tac-text group-hover:text-tac-primary transition-colors leading-snug">
                    {a.title}
                  </div>
                  {a.description && (
                    <div className="text-[14px] text-tac-dim mt-0.5 leading-snug">{a.description}</div>
                  )}
                  <div className="text-[12px] font-mono-tac text-tac-faint mt-1">
                    ~{a.radius_mi} mi radius · {a.created_by || "staff"}
                    {a.county ? ` · ${a.county.replace("_", " ")} Co.` : ""}
                    {a.updated_at && new Date(a.updated_at) - new Date(a.created_at) > 60000
                      ? ` · updated ${fmtDT(a.updated_at)}`
                      : ""}
                  </div>
                </button>
                <div className="flex items-center gap-3 mt-1.5">
                  <button
                    type="button"
                    data-testid={`evac-timeline-toggle-${a.id}`}
                    onClick={() => toggleTimeline(a.id)}
                    className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text"
                  >
                    <ClockCounterClockwise size={10} weight="bold" /> Timeline
                    <CaretDown
                      size={9}
                      weight="bold"
                      className={`transition-transform ${openTimelines.has(a.id) ? "rotate-180" : ""}`}
                    />
                  </button>
                  {a.source_url && (
                    <button
                      type="button"
                      data-testid={`sheriff-evac-source-${a.id}`}
                      onClick={() => setPerimUrl(a.source_url)}
                      className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text"
                    >
                      {a.source === "PERIMETER"
                        ? "Perimeter map"
                        : a.source === "CHELAN_EM"
                        ? "Chelan EM map"
                        : "Source"}{" "}
                      <ArrowSquareOut size={10} weight="bold" />
                    </button>
                  )}
                  {canPost && (!a.source || a.source === "MANUAL") && (
                    <button
                      type="button"
                      data-testid={`sheriff-evac-lift-${a.id}`}
                      onClick={() => lift(a.id)}
                      disabled={lifting === a.id}
                      className="ml-auto text-[12px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-danger disabled:opacity-50"
                    >
                      {lifting === a.id ? "Lifting…" : "Lift alert"}
                    </button>
                  )}
                </div>
                {openTimelines.has(a.id) && <EvacZoneTimeline zoneId={a.id} />}
                {roads.length > 0 && (
                  <div className="mt-1.5">
                    <button
                      type="button"
                      data-testid={`evac-roads-toggle-${a.id}`}
                      onClick={() => toggleRoads(a.id)}
                      className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-tac-warning hover:text-tac-text transition-colors"
                    >
                      <Barricade size={11} weight="fill" />
                      Road closures in this area · {roads.length}
                      <CaretDown
                        size={10}
                        weight="bold"
                        className={`transition-transform ${roadsOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {roadsOpen && (
                      <ul className="mt-1 border-l-2 border-tac-warning/40 pl-2 space-y-1" data-testid={`evac-roads-list-${a.id}`}>
                        {roads.map((c) => (
                          <li key={c.id}>
                            <button
                              type="button"
                              data-testid={`evac-road-item-${c.id}`}
                              onClick={() => (onClosureSelect ? onClosureSelect(c) : onFlyTo?.(c.lat, c.lon))}
                              className="w-full text-left text-[13px] text-tac-dim hover:text-tac-text transition-colors"
                            >
                              <span className="font-semibold text-tac-text">{c.name || "Road closure"}</span>
                              {c.milepost != null ? ` · MP ${c.milepost}` : ""}
                              <span className="font-mono-tac text-tac-faint"> · {c._d.toFixed(1)} mi from zone center · {c.src}</span>
                              {c.headline && (
                                <span className="block text-[12px] text-tac-faint leading-snug mt-0.5">
                                  {c.headline.length > 110 ? `${c.headline.slice(0, 110)}…` : c.headline}
                                </span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

              </li>
            );
              })}
            </React.Fragment>
          ))}
        </ul>
      )}
      {open && pastAlerts.length > 0 && (
        <div className="border-t border-tac-border/50">
          <button
            type="button"
            data-testid="evac-past-toggle"
            onClick={() => setPastOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-2 text-left"
          >
            <ClockCounterClockwise size={12} weight="bold" className="text-tac-faint" />
            <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-tac-dim">
              Rescinded &amp; Historical Zones
            </span>
            <span className="text-[12px] font-mono-tac text-tac-faint">· {pastAlerts.length}</span>
            <CaretDown
              size={11}
              weight="bold"
              className={`text-tac-dim ml-auto transition-transform ${pastOpen ? "rotate-180" : ""}`}
            />
          </button>
          {pastOpen && (
            <ul className="divide-y divide-tac-border/40 max-h-[280px] overflow-y-auto tac-scroll" data-testid="evac-past-list">
              {pastAlerts.map((a) => {
                const m = EVAC_LEVEL_META[a.level] || EVAC_LEVEL_META[1];
                const sMeta = evacStatusMeta(a);
                return (
                  <li key={a.id} data-testid={`evac-past-${a.id}`} className="px-4 py-2 opacity-60">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border"
                        style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}12` }}
                      >
                        {m.label}
                      </span>
                      <span
                        data-testid={`evac-status-chip-${a.id}`}
                        className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: sMeta.color }}
                      >
                        {sMeta.label}
                      </span>
                      {(a.lifted_at || a.updated_at) && (
                        <span className="text-[11px] font-mono-tac text-tac-faint ml-auto">
                          lifted {fmtDT(a.lifted_at || a.updated_at)}
                        </span>
                      )}
                    </div>
                    <div className="text-[14px] text-tac-dim leading-snug mt-0.5">{a.title}</div>
                    <button
                      type="button"
                      data-testid={`evac-timeline-toggle-${a.id}`}
                      onClick={() => toggleTimeline(a.id)}
                      className="inline-flex items-center gap-1 mt-1 text-[11px] font-bold uppercase tracking-wider text-tac-faint hover:text-tac-text"
                    >
                      <ClockCounterClockwise size={9} weight="bold" /> Timeline
                      <CaretDown
                        size={8}
                        weight="bold"
                        className={`transition-transform ${openTimelines.has(a.id) ? "rotate-180" : ""}`}
                      />
                    </button>
                    {openTimelines.has(a.id) && <EvacZoneTimeline zoneId={a.id} />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {open && (
        <div className="px-4 py-1.5 text-[11px] font-mono-tac text-tac-faint border-t border-tac-border/40">
          Auto-synced from County EM (Perimeter) + verified staff posts · confirm with local Sheriff / EM
        </div>
      )}
      {perimUrl && (
        <PerimeterMapModal url={perimUrl} title="County EM Perimeter Map" onClose={() => setPerimUrl(null)} />
      )}
    </section>
  );
}
